import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AddNewCardService } from './service/add-new-card.service';
import { environment } from '../../../../../../../../environments/environment';
import { CardsStateService } from '../../services/cards-state.service';
import { loadStripe, Stripe, StripeElements, StripeCardElement, SetupIntentResult } from '@stripe/stripe-js';
import { IntentPaymentResponse } from './interfaces/add-new-card.interface';
import { shareReplay } from 'rxjs';

// Stripe types are already imported from @stripe/stripe-js

@Component({
  selector: 'stripe-add-new-card',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './add-new-card.component.html',
  styleUrl: './add-new-card.component.css'
})
export class AddNewCardComponent implements OnInit, AfterViewInit, OnDestroy {
  cardForm: FormGroup;
  showModal = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  cardBrand: string = '';
  cardBrandColor: string = 'bg-gray-500';
  stripePublicKey = environment.stripePagoPublicKey;

  // Propiedades para Stripe
  stripe: Stripe | null = null;
  elements: StripeElements | null = null;
  cardElement: StripeCardElement | null = null;

  @Output() cardAdded = new EventEmitter<void>();
  @Output() modalOpened = new EventEmitter<void>();

  // Mapeo de colores por marca de tarjeta (compatibles con Stripe)
  private brandColors: { [key: string]: string } = {
    'visa': '#1a1f71',
    'mastercard': '#eb001b',
    'amex': '#006fcf',
    'diners': '#0079be',
    'discover': '#ff6000',
    'jcb': '#006cbe',
    'unionpay': '#e21836',
    'unknown': '#6b7280',
    'default': '#6b7280'
  };

  constructor(
    private fb: FormBuilder,
    private addNewCardService: AddNewCardService,
    private cardsStateService: CardsStateService
  ) {
    this.cardForm = this.fb.group({
      'cardholder-name': ['', Validators.required],
      cardNumber: ['', [Validators.required, Validators.pattern('^[0-9]{16}$')]],
      expirationDate: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[0-2])\/([0-9]{2})$')]],
      securityCode: ['', [Validators.required, Validators.pattern('^[0-9]{3,4}$')]]
    });
  }

  ngOnInit(): void {
    // Inicializar Stripe SDK
    this.initStripe();
  }

  ngAfterViewInit(): void {
    // Si el modal está abierto, configurar el formulario de tarjeta
    if (this.showModal) {
      this.setupStripeElements();
    }
  }

  ngOnDestroy(): void {
    // Limpiar Stripe Elements si existe
    this.cleanupStripeElements();
  }

  private async initStripe(): Promise<void> {
    try {
      this.stripe = await loadStripe(this.stripePublicKey);
      if (!this.stripe) {
        throw new Error('Failed to load Stripe');
      }
    } catch (error) {
      console.error('Error initializing Stripe:', error);
      this.errorMessage = 'Error al inicializar el servicio de pago';
    }
  }

  openModal(): void {
    this.showModal = true;
    this.resetForm();
    // Configurar Stripe Elements después de que el modal esté visible
    setTimeout(() => {
      this.setupStripeElements();
      this.modalOpened.emit();
    }, 100);
  }

  closeModal(): void {
    this.cleanupStripeElements();
    this.showModal = false;
    this.resetForm();
  }

  resetForm(): void {
    this.cardForm.reset();
    this.errorMessage = '';
    this.successMessage = '';
    this.cardBrand = '';
    this.cardBrandColor = 'bg-gray-500';
  }

  private async setupStripeElements(): Promise<void> {
    if (!this.stripe) {
      console.error('Stripe no está inicializado');
      this.errorMessage = 'Error al inicializar el servicio de pago';
      return;
    }

    try {
      // Limpiar elementos anteriores si existen
      await this.cleanupStripeElements();

      // Crear elementos de Stripe
      this.elements = this.stripe.elements();
      // console.log('Stripe Elements creado:', this.elements);

      // Configurar el elemento de tarjeta con estilos mejorados
      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#ffffff',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            '::placeholder': {
              color: '#9ca3af',
            },
            backgroundColor: 'transparent',
            padding: '12px',
          },
          invalid: {
            color: '#ef4444',
            iconColor: '#ef4444',
          },
          complete: {
            color: '#10b981',
            iconColor: '#10b981',
          },
        },
        hidePostalCode: true,
      });

      // console.log('Card Element creado:', this.cardElement);

      // Esperar un momento para asegurar que el DOM esté listo
      setTimeout(() => {
        const cardContainer = document.getElementById('stripe-card-element');
        // console.log('Container encontrado:', cardContainer);

        if (cardContainer) {
          // Limpiar el contenedor antes de montar
          cardContainer.innerHTML = '';

          this.cardElement!.mount('#stripe-card-element');
          // console.log('Stripe Element montado exitosamente');

          // Escuchar cambios en el elemento de tarjeta
          this.cardElement!.on('change', (event) => {
            // console.log('Stripe Element change event:', event);

            if (event.brand) {
              this.cardBrand = event.brand;
              this.cardBrandColor = this.brandColors[event.brand] || this.brandColors['default'];
            }

            if (event.error) {
              this.errorMessage = event.error.message || 'Error en los datos de la tarjeta';
            } else {
              this.errorMessage = '';
            }
          });

          // Escuchar cuando el elemento esté listo
          this.cardElement!.on('ready', () => {
            // console.log('Stripe Element está listo para usar');
          });

          // Escuchar cuando el elemento reciba foco
          this.cardElement!.on('focus', () => {
            // console.log('Stripe Element recibió foco');
          });
        } else {
          console.error('No se encontró el contenedor stripe-card-element');
          this.errorMessage = 'Error: No se pudo encontrar el contenedor del formulario';
        }
      }, 200);
    } catch (error) {
      console.error('Error al configurar Stripe Elements:', error);
      this.errorMessage = 'Error al configurar el formulario de tarjeta';
    }
  }

  private async cleanupStripeElements(): Promise<void> {
    if (this.cardElement) {
      try {
        this.cardElement.destroy();
      } catch (error) {
        console.error('Error al destruir Stripe Elements:', error);
      } finally {
        this.cardElement = null;
      }
    }
    this.elements = null;
  }

  onCardNumberChange(event: any): void {
    const cardNumber = event.target.value.replace(/\s/g, '');

    // Detectar la marca de la tarjeta basado en los primeros dígitos
    if (cardNumber.length >= 4) {
      this.detectCardBrand(cardNumber);
    } else {
      this.cardBrand = '';
      this.cardBrandColor = 'bg-gray-500';
    }
  }

  detectCardBrand(cardNumber: string): void {
    // Limpiar el número de tarjeta
    const cleanNumber = cardNumber.replace(/\s+/g, '');

    // Patrones para detectar marcas de tarjetas (compatibles con Stripe)
    const patterns = {
      'visa': /^4/,
      'mastercard': /^5[1-5]|^2[2-7]/,
      'amex': /^3[47]/,
      'diners': /^3[0689]/,
      'discover': /^6(?:011|5)/,
      'jcb': /^35/,
      'unionpay': /^62/
    };

    // Detectar la marca
    for (const [brand, pattern] of Object.entries(patterns)) {
      if (pattern.test(cleanNumber)) {
        this.cardBrand = brand;
        this.cardBrandColor = this.brandColors[brand] || this.brandColors['default'];
        return;
      }
    }

    // Si no se detecta ninguna marca conocida
    this.cardBrand = 'unknown';
    this.cardBrandColor = this.brandColors['default'];
  }

  async saveCard(): Promise<void> {
    if (!this.stripe || !this.cardElement) {
      this.errorMessage = 'El formulario de tarjeta no está disponible';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      // Obtener el nombre del titular
      const cardholderNameInput = document.getElementById('cardholder-name') as HTMLInputElement;
      const cardholderName = cardholderNameInput?.value?.trim();

      if (!cardholderName) {
        this.errorMessage = 'El nombre del titular es requerido';
        this.isLoading = false;
        return;
      }

      // Crear el payment method con Stripe
      const { paymentMethod, error } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement,
        billing_details: {
          name: cardholderName,
        },
      });
      this.addNewCardService.intentCard().pipe().subscribe({
        next: async (response: IntentPaymentResponse) => {
          if (response.status) {
            // this.successMessage = 'Intent de pago creado exitosamente';
            const saveIntent = await this.confirmStripeSetupIntent(response.data.client_secret);

            if (saveIntent.error) {
              this.errorMessage = saveIntent.error.message || 'Error al guardar la tarjeta';
              this.isLoading = false;
              return;
            } else {
              const paymentMethodId = saveIntent.setupIntent?.payment_method || paymentMethod!.id;
              const saveRequest = {
                payment_method_id: paymentMethodId as string,
              };

              // Guardar la tarjeta
              this.addNewCardService.saveCard(saveRequest).pipe(

              ).subscribe({
                next: (response) => {
                  this.successMessage = 'Tarjeta guardada exitosamente';
                  this.isLoading = false;

                  // Actualizar el estado de las tarjetas después de agregar una nueva
                  this.cardsStateService.refreshCards();
                  // Emitir evento de tarjeta agregada
                  this.cardAdded.emit();

                  setTimeout(() => {
                    this.closeModal();
                  }, 2000);
                },
                error: (error) => {
                  console.error('Error al guardar la tarjeta:', error);
                  this.errorMessage = error.error?.message || 'Error al guardar la tarjeta';
                  this.isLoading = false;
                }
              });
            }


          } else {
            this.errorMessage = response.message || 'Error al crear el intent de pago';
          }
        },
        error: (error) => {
          console.error('Error al crear el intent de pago:', error);
          this.errorMessage = error.error?.message || 'Error al crear el intent de pago';
        }
      })

      if (error) {
        this.errorMessage = error.message || 'Error al procesar la tarjeta';
        this.isLoading = false;
        return;
      }

      if (!paymentMethod) {
        this.errorMessage = 'Error al generar el payment method de la tarjeta';
        this.isLoading = false;
        return;
      }

      // Preparar la solicitud para guardar la tarjeta

    } catch (error) {
      console.error('Error en el proceso de guardado:', error);
      this.errorMessage = 'Error al procesar la tarjeta';
      this.isLoading = false;
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }
  async confirmStripeSetupIntent(clientSecret: string): Promise<SetupIntentResult> {
    const cardholderName = this.cardForm.get('cardholder-name')?.value?.trim() || '';
    return await this.stripe!.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card: this.cardElement!,
          billing_details: { name: cardholderName },
        },
      }
    );
  }
}
